import { t } from 'i18next';
import { SupportFormValues, SupportTicketResponse } from './types';

/** Форматирует дату в строку вида `DD.MM.YYYY HH:mm`. */
export const formatProblemTime = (date: Date): string => {
  const pad = (value: number): string => value.toString().padStart(2, '0');

  return (
    `${pad(date.getDate())}.${pad(
      date.getMonth() + 1
    )}.${date.getFullYear()} ` +
    `${pad(date.getHours())}:${pad(date.getMinutes())}`
  );
};

const DATE_TIME_REGEX = /^(\d{2})\.(\d{2})\.(\d{4}) (\d{2}):(\d{2})$/;

/**
 * Маска ввода даты-времени: оставляет только цифры и автоматически
 * подставляет разделители в формат `DD.MM.YYYY HH:mm`.
 */
export const maskDateTime = (value: string): string => {
  const digits = value.replace(/\D/g, '').slice(0, 12);

  return digits.split('').reduce((acc, char, index) => {
    if (index === 2 || index === 4) return `${acc}.${char}`;
    if (index === 8) return `${acc} ${char}`;
    if (index === 10) return `${acc}:${char}`;
    return `${acc}${char}`;
  }, '');
};

/** Проверяет, что строка — корректные дата и время в формате `DD.MM.YYYY HH:mm`. */
export const isValidDateTime = (value: string): boolean => {
  const match = value.match(DATE_TIME_REGEX);
  if (!match) return false;

  const [, day, month, year, hours, minutes] = match.map(Number);
  if (month < 1 || month > 12) return false;
  const daysInMonth = new Date(year, month, 0).getDate();
  if (day < 1 || day > daysInMonth) return false;
  if (hours > 23 || minutes > 59) return false;

  return true;
};

/**
 * Собирает FormData для отправки на /support-form.
 * Необязательные пустые поля и файлы не добавляются.
 */
export const buildFormData = (
  values: SupportFormValues,
  files: File[]
): FormData => {
  const formData = new FormData();

  const requiredEntries: Array<[keyof SupportFormValues, string]> = [
    ['full_name', values.full_name],
    ['phone', values.phone],
    ['pharmacy_name', values.pharmacy_name],
    ['pharmacy_source_code', values.pharmacy_source_code],
    ['problem_type', values.problem_type],
    ['description', values.description],
    ['error_time', values.error_time],
  ];

  const optionalEntries: Array<[keyof SupportFormValues, string]> = [
    ['order_number', values.order_number],
    ['actions_done', values.actions_done],
    ['extra_info', values.extra_info],
    ['anydesk_id', values.anydesk_id],
  ];

  requiredEntries.forEach(([key, value]) => formData.append(key, value.trim()));
  optionalEntries.forEach(([key, value]) => {
    const trimmed = value.trim();
    if (trimmed) formData.append(key, trimmed);
  });

  files.forEach((file) => formData.append('files', file));

  return formData;
};

/**
 * Приводит поле `detail` ответа к строке.
 * Сервис может вернуть строку (наш формат ошибки) либо массив объектов валидации
 * FastAPI (`[{ msg, loc, ... }]`) — массив/объект нельзя рендерить как React-child,
 * поэтому такие случаи сводим к читаемой строке или к общему тексту.
 */
export const normalizeErrorDetail = (detail: unknown): string => {
  if (typeof detail === 'string' && detail.trim()) {
    return detail;
  }

  if (Array.isArray(detail)) {
    const messages = detail
      .map((item) =>
        item && typeof item === 'object' && 'msg' in item
          ? String((item as { msg: unknown }).msg)
          : ''
      )
      .filter(Boolean);

    if (messages.length) {
      return messages.join('; ');
    }
  }

  return t('SupportWidget.ERROR_GENERIC');
};

/** Достаёт человекочитаемый текст ошибки из ответа сервиса либо отдаёт общий текст. */
export const getServerErrorMessage = (error: unknown): string => {
  const detail = (
    error as {
      response?: { data?: Partial<SupportTicketResponse> };
    }
  )?.response?.data?.detail;

  return normalizeErrorDetail(detail);
};
